import { Link } from 'react-router-dom';
import type { ArticleData } from '@/mocks/articles';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://cssbuy.today';

interface ArticleLayoutProps {
  article: ArticleData;
}

export default function ArticleLayout({ article }: ArticleLayoutProps) {
  const articleUrl = `${SITE_URL}/${article.slug}`;
  const hasFaqSection = article.sections.some(s => s.type === 'faq');

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.image,
    url: articleUrl,
    datePublished: '2026-06-01',
    dateModified: '2026-06-17',
    inLanguage: 'pt',
    author: {
      '@type': 'Organization',
      name: 'cssbuy.today',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'cssbuy.today',
      url: SITE_URL,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
  };

  const faqSchema = hasFaqSection ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: (article.sections.find(s => s.type === 'faq')?.qa || []).map((item: { q: string; a: string }) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  } : null;

  const schemas = faqSchema ? [articleSchema, faqSchema] : [articleSchema];
  const schemaScript = JSON.stringify(schemas);

  const pageTitle = `${article.title} | cssbuy.today`;

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={article.description} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={article.description} />
      <meta property="og:url" content={articleUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:image" content={article.image} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={article.description} />
      <link rel="canonical" href={articleUrl} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: schemaScript }} />

      <article className="pt-[120px] pb-20">
        <div className="max-w-[800px] mx-auto px-6 md:px-12">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-foreground-500 hover:text-primary-500 transition-colors duration-200"
            >
              <i className="ri-arrow-left-line"></i>
              Voltar ao Início
            </Link>
          </nav>

          {/* Header */}
          <header className="mb-10">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                Atualizado em Junho 2026
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-global-line"></i>
                Português
              </span>
            </div>
          </header>

          {/* Hero Image */}
          <div className="rounded-2xl overflow-hidden mb-12 h-[300px] md:h-[400px]">
            <img
              src={article.image}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>

          {/* Content Sections */}
          {article.sections.map((section, idx) => {
            switch (section.type) {
              case 'intro':
                return (
                  <section key={idx} className="mb-10">
                    <p className="text-foreground-700 leading-relaxed text-base md:text-lg">
                      {section.content}
                    </p>
                  </section>
                );

              case 'heading':
                return (
                  <section key={idx} className="mb-4 mt-10">
                    <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">
                      {section.heading}
                    </h2>
                  </section>
                );

              case 'paragraph':
                return (
                  <section key={idx} className="mb-5">
                    <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                      {section.content}
                    </p>
                  </section>
                );

              case 'list':
                return (
                  <section key={idx} className="mb-8">
                    <ol className="space-y-3 list-none pl-0">
                      {section.items?.map((item, i) => (
                        <li key={i} className="flex gap-3 text-foreground-700 text-sm md:text-base leading-relaxed">
                          <span className="shrink-0 w-6 h-6 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center text-xs font-bold mt-0.5">
                            {i + 1}
                          </span>
                          {item}
                        </li>
                      ))}
                    </ol>
                  </section>
                );

              case 'table':
                return (
                  <section key={idx} className="mb-8">
                    <div className="overflow-x-auto rounded-xl border border-background-200">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-background-100">
                            {section.headers?.map((h, i) => (
                              <th key={i} className="text-left px-5 py-3.5 font-semibold text-foreground-800 whitespace-nowrap">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {section.rows?.map((row, ri) => (
                            <tr key={ri} className="border-t border-background-200">
                              {row.map((cell, ci) => (
                                <td key={ci} className={`px-5 py-3.5 whitespace-nowrap ${ci === 0 ? 'font-semibold text-foreground-800' : 'text-foreground-700'} ${ci === row.length - 1 && (cell.includes('/') || cell.includes('9.')) ? 'text-accent-600 font-medium' : ''}`}>{cell}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-foreground-500 text-xs mt-3 italic">* Dados atualizados em Junho 2026.</p>
                  </section>
                );

              case 'faq':
                return (
                  <section key={idx} className="mb-8" aria-label="Perguntas Frequentes">
                    <div className="space-y-4">
                      {section.qa?.map((item, i) => (
                        <div key={i} className="bg-background-100 rounded-xl p-5 md:p-6">
                          <h3 className="font-semibold text-foreground-800 mb-2 text-sm md:text-base">{item.q}</h3>
                          <p className="text-foreground-600 text-sm leading-relaxed">{item.a}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                );

              case 'cta':
                return (
                  <section key={idx} className="bg-foreground-900 rounded-2xl p-8 md:p-10 text-center my-12">
                    <h2 className="font-heading text-2xl md:text-3xl text-white mb-3">Pronto para Começar?</h2>
                    <p className="text-white/70 text-sm md:text-base mb-6 max-w-lg mx-auto">{section.content}</p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                      <a
                        href="https://kaspreadsheet.com/"
                        target="_blank"
                        rel="noopener nofollow"
                        className="px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-400 transition-all duration-200 whitespace-nowrap"
                      >
                        CSSBuy Spreadsheet
                      </a>
                      <a
                        href="https://kaspreadsheet.com/"
                        target="_blank"
                        rel="noopener nofollow"
                        className="px-6 py-3 rounded-full border border-white/30 text-white font-semibold text-sm hover:bg-white/10 transition-all duration-200 whitespace-nowrap"
                      >
                        Buy It Now
                      </a>
                    </div>
                  </section>
                );

              default:
                return null;
            }
          })}

          {/* Back to Home */}
          <div className="mt-12 pt-8 border-t border-background-200">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-foreground-500 hover:text-primary-500 transition-colors duration-200 text-sm"
            >
              <i className="ri-arrow-left-line"></i>
              Voltar ao Início — cssbuy.today
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}